import { ESP32BLE } from "@/services/domainControl/define";
import {
  BleClient,
  numberToUUID,
  dataViewToText,
  textToDataView,
  BleDevice,
} from "@capacitor-community/bluetooth-le";
import {
  BluetoothRawOption,
  convertToBuffer,
  convertToString,
  getAllServicesBluetoothServiceUUID,
  JSONParse,
  notifyErrorBluetooth,
  Options,
  ReadOptions,
} from "../bluetooth";
import { reject } from "lodash-es";

export type BluetoothPluginDevice = BleDevice &
  BluetoothDevice & {
    id: string;
  };

export type ListenerItem = {
  listener: (balue: any) => void;
  serviceBluetoothServiceUUID: BluetoothServiceUUID;
  characteristicBluetoothServiceUUID: BluetoothServiceUUID;
};

const getUUID = (uuid: BluetoothServiceUUID) => {
  return typeof uuid === "number" ? numberToUUID(uuid) : uuid;
};

export class BluetoothPluginProxy {
  public isConnected = false;
  public device = null as any as BluetoothPluginDevice; // 蓝牙设备实例
  public bluetoothRawOptions = null as any as BluetoothRawOption[]; // 蓝牙配置
  private queue: Array<() => Promise<void>> = [];
  private notificationListeners = [] as ListenerItem[]; // 所有订阅的通知
  private isProcessing = false;

  constructor({ bluetoothRawOptions = [] }: Options) {
    this.bluetoothRawOptions = bluetoothRawOptions;
  }

  private async processQueue() {
    if (this.isProcessing || this.queue.length === 0) return;
    this.isProcessing = true;
    const task = this.queue.shift();
    if (task) {
      try {
        await task();
      } catch (error) {
        notifyErrorBluetooth(error as Error, "processQueue");
      }
      this.isProcessing = false;
      this.processQueue();
    }
  }

  // request({ bluetoothRawOptions }: Options = {}) {
  //   this.bluetoothRawOptions = bluetoothRawOptions || this.bluetoothRawOptions;
  //   const options = {
  //     name: this.bluetoothRawOptions.map((item) => item.name)[0] || "",
  //     // services: ESP32BLE.services.map((s) => getUUID(s.uuid)),
  //     optionalServices: getAllServicesBluetoothServiceUUID(
  //       this.bluetoothRawOptions
  //     ).map((uuid) => getUUID(uuid)),
  //   };
  //   console.log(
  //     "[BOWEN_LOG] 🚀 ~~ BluetoothPluginProxy ~~ request ~~ options:",
  //     options
  //   );
  //   return BleClient.initialize({ androidNeverForLocation: true })
  //     .then(() =>
  //       BleClient.requestDevice(options).then((device) => {
  //         this.device = {
  //           ...device,
  //           id: device.deviceId,
  //         } as BluetoothPluginDevice;
  //         return device;
  //       })
  //     )
  //     .catch((error) => {
  //       if (error.message.includes("requestDevice cancelled")) {
  //         throw error; // 用户取消
  //       }
  //       notifyErrorBluetooth(error, "requestDevice");
  //       throw error;
  //     });
  // }

  request({ bluetoothRawOptions }: Options = {}) {
    this.bluetoothRawOptions = bluetoothRawOptions || this.bluetoothRawOptions;
    // const options = {
    //   name: this.bluetoothRawOptions.map((item) => item.name)[0] || "",
    //   // services: ESP32BLE.services.map((s) => getUUID(s.uuid)),
    //   optionalServices: getAllServicesBluetoothServiceUUID(
    //     this.bluetoothRawOptions
    //   ).map((uuid) => getUUID(uuid)),
    // };

    return BleClient.initialize({ androidNeverForLocation: true })
      .then(() =>
        this.scanForDevice().then((device) => {
          console.log(
            "[BOWEN_LOG] 🚀 ~~ BluetoothPluginProxy ~~ request ~~ device:",
            device
          );
          if (!device) {
            throw new Error("未找到设备 No device found");
          }
          this.device = {
            ...device,
            id: device?.deviceId,
          } as BluetoothPluginDevice;
          return device;
        })
      )
      .catch((error) => {
        if (error.message.includes("requestDevice cancelled")) {
          throw error; // 用户取消
        }
        notifyErrorBluetooth(error, "requestDevice");
        throw error;
      });
  }

  // 扫描设备方法
  private async scanForDevice(): Promise<BleDevice | null> {
    return new Promise((resolve) => {
      let timeoutId: any;
      const stopScanning = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        BleClient.stopLEScan();
      };

      // 设置10秒超时
      timeoutId = setTimeout(() => {
        stopScanning();
        resolve(null);
      }, 10000);

      const options = {
        name: this.bluetoothRawOptions.map((item) => item.name)[0] || "",
        // services: ESP32BLE.services.map((s) => getUUID(s.uuid)),
        optionalServices: getAllServicesBluetoothServiceUUID(
          this.bluetoothRawOptions
        ).map((uuid) => getUUID(uuid)),
      };
      const deviceName =
        this.bluetoothRawOptions.map((item) => item.name)[0] || "";
      // 开始扫描
      BleClient.requestLEScan(options, (scanResult) => {
        console.log(
          "[BOWEN_LOG] 🚀 ~~ BluetoothPluginProxy ~~ BleClient.requestLEScan ~~ scanResult:",
          scanResult
        );
        if (scanResult.device.name === deviceName) {
          stopScanning();
          resolve(scanResult.device);
        }
      }).catch((error) => {
        console.error("Scan error:", error);
        stopScanning();
        reject(error);
      });
    });
  }

  async connect(onDisconnected?: () => void, retryCount = 3) {
    if (!this.device) {
      return Promise.reject("Device is not connected.");
    }

    for (let i = 0; i < retryCount; i++) {
      try {
        // 增加连接前的延迟时间
        await new Promise((resolve) => setTimeout(resolve, 500));
        await BleClient.connect(this.device.deviceId, () => {
          if (this.isConnected) {
            this.onDisconnected();
            onDisconnected?.();
          }
        });
        this.isConnected = true;
        console.log(`设备连接成功，尝试次数：${i + 1}`);
        return;
      } catch (error) {
        console.log(`第 ${i + 1} 次连接失败:`, error);
        if (i === retryCount - 1) {
          notifyErrorBluetooth(error as Error, "connect");
          throw error;
        }
        // // 连接失败后等待一段时间再重试
        // await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }
  }

  // 获取所有特征
  getCharacteristics() {
    return BleClient.getServices(this.device.deviceId).then((services) => {
      const characteristics = [] as any[];
      services.map((service) =>
        service.characteristics.map((characteristic) =>
          characteristics.push({
            ...characteristic,
            service: service,
          })
        )
      );
      return characteristics;
    });
  }

  // 获取所有服务
  getServices() {
    return BleClient.getServices(this.device.deviceId);
  }

  readData(
    serviceBluetoothServiceUUID: BluetoothServiceUUID,
    characteristicBluetoothServiceUUID: BluetoothServiceUUID,
    options?: ReadOptions
  ) {
    return BleClient.read(
      this.device.deviceId,
      getUUID(serviceBluetoothServiceUUID),
      getUUID(characteristicBluetoothServiceUUID)
    )
      .then((value) => {
        if (!value) return null;
        return options?.showRawRes
          ? convertToString(value)
          : JSONParse(convertToString(value)); // parse JSON
      })
      .catch((error) => {
        !options?.notShowNotify && notifyErrorBluetooth(error, "readData");
        throw error;
      });
  }

  writeData(
    data: string | number | ArrayBuffer,
    serviceBluetoothServiceUUID: BluetoothServiceUUID,
    characteristicBluetoothServiceUUID: BluetoothServiceUUID,
    options?: ReadOptions
  ) {
    // to json string
    data = options?.showRawRes ? data : JSON.stringify(data);
    // to DataView
    const bufferData =
      typeof data === "string" || typeof data === "number"
        ? convertToBuffer(data, options)
        : data;
    const dataview = new DataView(
      (bufferData as Uint8Array).buffer || bufferData
    );
    // console.log("[BOWEN_LOG] 🚀 ~~ BluetoothPluginProxy ~~ dataview:", dataview);
    return BleClient.write(
      this.device.deviceId,
      getUUID(serviceBluetoothServiceUUID),
      getUUID(characteristicBluetoothServiceUUID),
      dataview
    ).catch((error) => {
      !options?.notShowNotify && notifyErrorBluetooth(error, "writeData");
      throw error;
    });
  }

  // 顺序执行 写入数据
  async queuePerformWriteData(
    data: string | number | ArrayBuffer,
    serviceBluetoothServiceUUID: BluetoothServiceUUID,
    characteristicBluetoothServiceUUID: BluetoothServiceUUID,
    options?: ReadOptions
  ) {
    return new Promise<any>((resolve, reject) => {
      this.queue.push(async () => {
        try {
          const res = await this.writeData(
            data,
            serviceBluetoothServiceUUID,
            characteristicBluetoothServiceUUID,
            options
          );
          resolve(res);
        } catch (error) {
          reject(error);
        }
      });
      this.processQueue();
    });
  }

  // 顺序执行 读取数据
  async queuePerformReadData(
    serviceBluetoothServiceUUID: BluetoothServiceUUID,
    characteristicBluetoothServiceUUID: BluetoothServiceUUID,
    options?: ReadOptions
  ) {
    return new Promise<any>((resolve, reject) => {
      this.queue.push(async () => {
        try {
          const res = await this.readData(
            serviceBluetoothServiceUUID,
            characteristicBluetoothServiceUUID,
            options
          );
          resolve(res);
        } catch (error) {
          reject(error);
        }
      });
      this.processQueue();
    });
  }

  startNotifications(
    listener: (value: string, event: Event) => void,
    serviceBluetoothServiceUUID: BluetoothServiceUUID,
    characteristicBluetoothServiceUUID: BluetoothServiceUUID,
    options?: ReadOptions
  ) {
    const curListener = (value: DataView) => {
      if (!value?.byteLength) return;
      listener(
        options?.showRawRes
          ? convertToString(value)
          : JSONParse(convertToString(value)),
        {
          target: {
            uuid: characteristicBluetoothServiceUUID,
          },
        } as any as Event
      );
    };

    return BleClient.startNotifications(
      this.device.deviceId,
      getUUID(serviceBluetoothServiceUUID),
      getUUID(characteristicBluetoothServiceUUID),
      curListener
    )
      .then(() => {
        // record
        this.notificationListeners.push({
          serviceBluetoothServiceUUID,
          characteristicBluetoothServiceUUID,
          listener: curListener,
        });
      })
      .catch((error) => {
        notifyErrorBluetooth(error, "startNotifications");
        throw error;
      });
  }

  stopNotifications(
    serviceBluetoothServiceUUID: BluetoothServiceUUID,
    characteristicBluetoothServiceUUID: BluetoothServiceUUID,
    listener?: (event: Event) => void
  ) {
    listener; // don't need listener, just stop all of characteristicBluetoothServiceUUID notifications
    return BleClient.stopNotifications(
      this.device.deviceId,
      getUUID(serviceBluetoothServiceUUID),
      getUUID(characteristicBluetoothServiceUUID)
    );
  }
  async stopAllNotifications() {
    const notificationListeners = [...this.notificationListeners];
    for (const item of notificationListeners) {
      try {
        await this.stopNotifications(
          item.serviceBluetoothServiceUUID,
          item.characteristicBluetoothServiceUUID
        );
        const index = this.notificationListeners.findIndex(
          (notify) =>
            notify.characteristicBluetoothServiceUUID ===
            item.characteristicBluetoothServiceUUID
        );
        if (index !== -1) this.notificationListeners.splice(index, 1);
      } catch (error) {
        console.log(
          "[BOWEN_LOG] 🚀 ~~ BluetoothProxy ~~ stopAllNotifications ~~ error:",
          error
        );
      }
    }
    console.log("All notifications stopped.", this.notificationListeners);
  }

  disconnect() {
    console.log(
      "[BOWEN_LOG] 🚀 ~~ BluetoothProxy ~~ disconnect ~~ disconnect:",
      this.device
    );
    if (!this.device) {
      return Promise.reject("Device is not connected.");
    }
    return BleClient.disconnect(this.device.deviceId);
  }

  onDisconnected(): void {
    this.isConnected = false;
    console.log(
      `device [${this.device.name}] [${this.device.deviceId}] disconnected`
    );
  }
}
